import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';
import { getCookie } from '@atlassian/browser-storage-controls';

export const CSRF_COOKIE_NAME = 'csrftoken';
export const CSRF_HEADER_NAME = 'X-CSRFToken';

export const isSameDomain = (url: string): boolean =>
  /^(?!https?:)/.test(url) || url.indexOf(window.location.origin) === 0;

export const jsonHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const getRequestHeaders = (
  url: string,
  csrftoken: string,
  includeB3Headers?: boolean,
  workspaceUUID?: string
) => {
  const headers: HeadersInit = {
    // Required for Django's request.is_ajax to work properly in some of our legacy views
    'X-Requested-With': 'XMLHttpRequest',

    // Used by RequestMetaMiddleware to identify API requests originating from Frontbucket, for use
    // in analytic events (facts)
    'X-Bitbucket-Frontend': 'frontbucket',
  };

  // never send tokens cross-(sub)domain
  if (isSameDomain(url)) {
    headers[CSRF_HEADER_NAME] = csrftoken;
  }

  // Add B3 tracing headers from active UFO trace context if requested
  if (includeB3Headers) {
    const b3Headers = getActiveTraceHttpRequestHeaders(url);
    if (b3Headers) {
      Object.assign(headers, b3Headers);
    }

    // Add workspace UUID header if available
    if (workspaceUUID) {
      headers['X-BB-Workspace-Id'] = workspaceUUID;
    }
  }

  return headers;
};

export interface AuthRequestOptions extends RequestInit {
  // Include B3 distributed tracing headers in the request
  // Used for propagating UFO trace context
  includeB3Headers?: boolean;
  // Workspace UUID to include in request headers
  workspaceUUID?: string;
}

export function authRequest(
  url: string,
  options: AuthRequestOptions = {},
  csrftoken: string = getCookie(CSRF_COOKIE_NAME) || ''
): Request {
  const { includeB3Headers, workspaceUUID, ...requestOptions } = options;
  const headers = getRequestHeaders(
    url,
    csrftoken,
    includeB3Headers,
    workspaceUUID
  );

  return new Request(url, {
    credentials: 'same-origin',
    ...requestOptions,
    headers: {
      ...headers,
      ...(requestOptions.headers || {}),
    },
  });
}
